/**
 * Route Protection Components
 *
 * ProtectedRoute: Requires any authenticated user (teacher or student)
 * SubscriptionRoute: Requires auth + valid subscription (admins bypass)
 * TeacherOnlyRoute: Requires teacher authentication only
 * AdminOnlyRoute: Requires admin status
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore, useSession } from '@/store';
import { useAdmin, useSubscription } from '@/hooks';
import { useFirebaseAuth } from '@/lib/firebase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Allows any authenticated user (teacher or student)
 *
 * For MVP, checks both Firebase auth (for teachers) and session userType.
 * Redirects to login page if not authenticated.
 * Does NOT check subscription - use SubscriptionRoute for that.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const { userType, isSessionValid } = useSession();
  const { isInitialized } = useFirebaseAuth();
  const location = useLocation();

  // Wait for Firebase to initialize before making auth decisions
  // This prevents false redirects when the auth state is still loading
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Check if user is authenticated:
  // - Teacher: Firebase auth (isAuthenticated) OR session userType
  // - Student: session userType and valid session
  const isLoggedIn =
    isAuthenticated ||
    userType === 'teacher' ||
    (userType === 'student' && isSessionValid());

  if (!isLoggedIn) {
    // Redirect to home (login page) while preserving the intended destination
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * SubscriptionRoute - Requires auth + valid subscription
 *
 * Used for main app features (whiteboard, etc.)
 * - Admins: bypass subscription check
 * - Students: bypass subscription check (using teacher's subscription)
 * - Teachers: must have valid trial or paid subscription
 */
export function SubscriptionRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const { userType, isSessionValid } = useSession();
  const { isInitialized } = useFirebaseAuth();
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const { access, isLoading: isSubLoading } = useSubscription();
  const location = useLocation();

  // Wait for Firebase to initialize before making auth decisions
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Check basic authentication first
  const isLoggedIn =
    isAuthenticated ||
    userType === 'teacher' ||
    (userType === 'student' && isSessionValid());

  if (!isLoggedIn) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Still loading admin/subscription status
  if (isAdminLoading || isSubLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Admins bypass subscription check
  if (isAdmin) {
    return <>{children}</>;
  }

  // Students bypass subscription check (they use teacher's subscription)
  if (userType === 'student') {
    return <>{children}</>;
  }

  // Teachers must have valid subscription
  if (userType === 'teacher' && !access.hasAccess) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * TeacherOnlyRoute - Requires teacher authentication
 *
 * Used for teacher-specific pages like animal code management.
 * Students are redirected to the whiteboard.
 */
export function TeacherOnlyRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const { userType } = useSession();
  const { isInitialized } = useFirebaseAuth();
  const location = useLocation();

  // Wait for Firebase to initialize before making auth decisions
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Teacher must be authenticated via Firebase
  const isTeacher = isAuthenticated && userType === 'teacher';

  if (!isAuthenticated) {
    // Not logged in at all - redirect to login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!isTeacher) {
    // Logged in but not a teacher (student) - redirect to whiteboard
    return <Navigate to="/whiteboard" replace />;
  }

  return <>{children}</>;
}

/**
 * StudentOnlyRoute - Requires student authentication
 *
 * Used for student-specific pages like the cast view.
 * Teachers are redirected to the workspace.
 */
export function StudentOnlyRoute({ children }: ProtectedRouteProps) {
  const { userType, isSessionValid } = useSession();
  const { isInitialized } = useFirebaseAuth();
  const location = useLocation();

  // Wait for Firebase to initialize before making auth decisions
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Student must have valid session
  const isStudent = userType === 'student' && isSessionValid();

  if (!userType) {
    // Not logged in at all - redirect to login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!isStudent) {
    // Logged in but not a student (teacher) - redirect to workspace
    return <Navigate to="/workspace" replace />;
  }

  return <>{children}</>;
}

/**
 * AdminOnlyRoute - Requires admin status
 *
 * Used for admin-only pages like managing other admins.
 * Non-admins are redirected to the whiteboard.
 */
export function AdminOnlyRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const { isInitialized } = useFirebaseAuth();
  const { isAdmin, isLoading } = useAdmin();
  const location = useLocation();

  // Wait for Firebase to initialize before making auth decisions
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/whiteboard" replace />;
  }

  return <>{children}</>;
}
